/**
 * Public pricing page — the canonical three-tier story (Free / Pay as you go /
 * Enterprise), driven entirely by src/config/pricing.ts so it never drifts from
 * the paywall, account billing, or plan badge.
 *
 *   Free        → start free (login)
 *   Credits     → buy credits (logged in: Stripe checkout picker; logged out: login)
 *   Enterprise  → contact us (reuses the inquiry form)
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PRICING_TIERS, CREDIT_MODEL_NOTE, type PricingTier, type TierId } from '../config/pricing';
import { BuyCreditsDialog } from '../components/billing/BuyCreditsDialog';
import { EnterpriseContactDialog } from '../components/billing/EnterpriseContactDialog';
import { UpgradeRequestForm } from '../components/billing/UpgradeRequestForm';
import { useAuth } from '../hooks/useAuth';

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
});

function PricingPage() {
  const { user } = useAuth();
  const loggedIn = !!user;
  const [buyOpen, setBuyOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    if (loggedIn && new URLSearchParams(window.location.search).has('canceled')) {
      window.location.replace('/account/billing?checkout=cancelled');
    }
  }, [loggedIn]);

  function handleCta(id: TierId) {
    if (id === 'FREE') {
      window.location.href = loggedIn ? '/' : '/login';
    } else if (id === 'CREDITS') {
      if (loggedIn) setBuyOpen(true);
      else window.location.href = '/login?next=/pricing';
    } else {
      setContactOpen(true);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h1 className="text-5xl font-bold tracking-tight">Simple, honest pricing</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Start free, pay only for what your videos use, and talk to us when your team scales.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
          {PRICING_TIERS.map((tier) => (
            <TierCard key={tier.id} tier={tier} onCta={() => handleCta(tier.id)} />
          ))}
        </div>

        <p className="text-center mt-10 text-sm text-muted-foreground max-w-2xl mx-auto">
          {CREDIT_MODEL_NOTE}
        </p>
      </div>

      <BuyCreditsDialog
        open={buyOpen}
        onOpenChange={setBuyOpen}
        fallback={
          <UpgradeRequestForm source="pricing_page" onSuccess={() => setTimeout(() => setBuyOpen(false), 1500)} />
        }
      />
      <EnterpriseContactDialog open={contactOpen} onOpenChange={setContactOpen} source="pricing_page" />
    </div>
  );
}

function TierCard({ tier, onCta }: { tier: PricingTier; onCta: () => void }) {
  return (
    <div
      className={`relative rounded-xl border p-6 flex flex-col ${
        tier.highlight ? 'border-primary shadow-lg shadow-primary/10' : 'border-border'
      }`}
    >
      {tier.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-0.5 text-xs font-semibold text-primary-foreground bg-primary">
          Most popular
        </span>
      )}
      <h3 className="text-2xl font-bold">{tier.name}</h3>
      <p className="text-sm text-muted-foreground mt-1 min-h-[2.5rem]">{tier.tagline}</p>
      <div className="mt-4">
        <span className="text-4xl font-bold">{tier.price}</span>
        <div className="text-xs text-muted-foreground mt-1">{tier.priceSuffix}</div>
      </div>

      <ul className="mt-6 space-y-2 text-sm flex-1">
        {tier.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            {f.included ? (
              <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            ) : (
              <X className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            )}
            <span className={f.included ? '' : 'text-muted-foreground line-through'}>{f.label}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <Button onClick={onCta} className="w-full" variant={tier.highlight ? 'default' : 'outline'}>
          {tier.ctaLabel}
        </Button>
      </div>
    </div>
  );
}
